import { useEffect, useRef, useMemo } from "react";

const TYPE_COLORS = {
  deity: "#7c3aed",
  festival: "#ea580c",
  vrata: "#ca8a04",
  story: "#2563eb",
  sadhana: "#059669",
  mantra: "#db2777",
  chakra: "#dc2626",
  element: "#65a30d",
  guna: "#0891b2",
  planet: "#4f46e5",
  nakshatra: "#9333ea",
  psychology: "#0d9488",
  principle: "#78716c",
};

function labelFor(node) {
  return node.title || node.id.split(":").pop()?.replace(/_/g, " ") || node.id;
}

/**
 * Lightweight force-directed graph (no external deps).
 */
export function GraphVisualizer({ nodes = [], edges = [], height = 320, className = "" }) {
  const svgRef = useRef(null);
  const simRef = useRef(null);

  const graphNodes = useMemo(
    () =>
      nodes.map((n, i) => ({
        ...n,
        x: 160 + 120 * Math.cos((i / Math.max(nodes.length, 1)) * Math.PI * 2),
        y: height / 2 + 100 * Math.sin((i / Math.max(nodes.length, 1)) * Math.PI * 2),
        vx: 0,
        vy: 0,
      })),
    [nodes, height]
  );

  useEffect(() => {
    if (!graphNodes.length) return;
    const width = 480;
    const centerX = width / 2;
    const centerY = height / 2;
    const list = graphNodes.map((n) => ({ ...n }));
    const idSet = new Set(list.map((n) => n.id));
    const links = edges.filter((e) => idSet.has(e.from) && idSet.has(e.to));

    let frame = 0;
    let raf;

    const tick = () => {
      for (const n of list) {
        n.vx += (centerX - n.x) * 0.002;
        n.vy += (centerY - n.y) * 0.002;
      }
      for (let i = 0; i < list.length; i++) {
        for (let j = i + 1; j < list.length; j++) {
          const a = list[i];
          const b = list[j];
          const dx = b.x - a.x;
          const dy = b.y - a.y;
          const dist = Math.sqrt(dx * dx + dy * dy) || 1;
          const force = 800 / (dist * dist);
          const fx = (dx / dist) * force;
          const fy = (dy / dist) * force;
          a.vx -= fx;
          a.vy -= fy;
          b.vx += fx;
          b.vy += fy;
        }
      }
      for (const e of links) {
        const a = list.find((n) => n.id === e.from);
        const b = list.find((n) => n.id === e.to);
        if (!a || !b) continue;
        const dx = b.x - a.x;
        const dy = b.y - a.y;
        const dist = Math.sqrt(dx * dx + dy * dy) || 1;
        const force = (dist - 90) * 0.04;
        const fx = (dx / dist) * force;
        const fy = (dy / dist) * force;
        a.vx += fx;
        a.vy += fy;
        b.vx -= fx;
        b.vy -= fy;
      }
      for (const n of list) {
        n.vx *= 0.85;
        n.vy *= 0.85;
        n.x += n.vx;
        n.y += n.vy;
        n.x = Math.max(40, Math.min(width - 40, n.x));
        n.y = Math.max(28, Math.min(height - 28, n.y));
      }

      const svg = svgRef.current;
      if (svg) {
        for (const n of list) {
          const g = svg.querySelector(`[data-node="${CSS.escape(n.id)}"]`);
          if (g) {
            const circle = g.querySelector("circle");
            const text = g.querySelector("text");
            if (circle) {
              circle.setAttribute("cx", String(n.x));
              circle.setAttribute("cy", String(n.y));
            }
            if (text) {
              text.setAttribute("x", String(n.x));
              text.setAttribute("y", String(n.y + 4));
            }
          }
        }
        const lines = svg.querySelectorAll("line[data-edge]");
        lines.forEach((line) => {
          const [from, to] = line.getAttribute("data-edge").split("|");
          const a = list.find((n) => n.id === from);
          const b = list.find((n) => n.id === to);
          if (a && b) {
            line.setAttribute("x1", String(a.x));
            line.setAttribute("y1", String(a.y));
            line.setAttribute("x2", String(b.x));
            line.setAttribute("y2", String(b.y));
          }
        });
      }

      frame += 1;
      if (frame < 120) raf = requestAnimationFrame(tick);
    };

    raf = requestAnimationFrame(tick);
    simRef.current = list;
    return () => cancelAnimationFrame(raf);
  }, [graphNodes, edges, height]);

  if (!nodes.length) {
    return (
      <p className={`text-sm text-muted-foreground ${className}`}>No graph nodes to display.</p>
    );
  }

  const width = 480;

  return (
    <svg
      ref={svgRef}
      viewBox={`0 0 ${width} ${height}`}
      className={`w-full max-w-lg mx-auto border rounded-md bg-muted/20 ${className}`}
      role="img"
      aria-label="Tattva graph preview"
    >
      {edges.map((e) => (
        <line
          key={`${e.from}-${e.to}-${e.relation}`}
          data-edge={`${e.from}|${e.to}`}
          x1={width / 2}
          y1={height / 2}
          x2={width / 2}
          y2={height / 2}
          stroke="#94a3b8"
          strokeWidth={1}
          opacity={0.7}
        />
      ))}
      {graphNodes.map((n) => (
        <g key={n.id} data-node={n.id}>
          <circle
            cx={n.x}
            cy={n.y}
            r={n.id.includes(":") && n.type ? 10 : 8}
            fill={TYPE_COLORS[n.type] || "#64748b"}
            stroke="#fff"
            strokeWidth={1.5}
          />
          <text
            x={n.x}
            y={n.y + 4}
            textAnchor="middle"
            fontSize={8}
            fill="#334155"
            pointerEvents="none"
          >
            {labelFor(n).slice(0, 12)}
          </text>
        </g>
      ))}
    </svg>
  );
}
